import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { loadEntity } from "src/pages/EntityCardPage/apiEntityCard";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { loadChildAttributes } from "src/pages/EntityFiltersPage/EntityList/EntityCopyButton/apiEntityCopy";
import { flattenItems } from "src/common/flattenItems";
import { TableStore } from "src/components/tables/TableStore";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { ZUid } from "src/types/zUid";
import { MultiStepTaskStore } from "src/components/MultiStepTask/MultiStepTaskStore";
import { RemoteData } from "src/common/RemoteData";
import { delay } from "src/common/delay";
import { ZEntityFiltersChild } from "../../ChildEntitiesStore";
import { copyEntitiesWithParents } from "../apiCopyEntities";

const enum CopyStep {
  step1,
  copy,
}

export class CopyInStore {
  constructor(
    public readonly entityId: ZUid,
    public readonly chilEntTableStore: TableStore<
      ZEntityRow,
      ZEntityFiltersChild
    >,
    public readonly parentObjId: ZUid,
  ) {
    makeAutoObservable(this);
  }

  taskStore = new MultiStepTaskStore();

  objectId: ZUid | null = null;

  setObjectId(id: ZUid) {
    this.objectId = id;
  }

  childAttributes: ZUid[] = [];

  setChildAttributes(attrs: ZUid[]) {
    this.childAttributes = attrs;
  }

  pageStore: EntityFiltersPageStore | null = null;

  async init() {
    try {
      const [{ objectId }, childAttrs] = await Promise.all([
        loadEntity(this.entityId),
        loadChildAttributes(this.parentObjId),
      ]);
      this.setObjectId(objectId);
      this.setChildAttributes(
        flattenItems(childAttrs, "linkedAttributes")?.map(
          ({ attributeId }) => attributeId,
        ) ?? [],
      );
      this.pageStore = new EntityFiltersPageStore({
        objectId,
        selectionSettings: { selectionType: "checkbox" },
        onRowClick: (row) => {
          const tableStore = this.pageStore?.tableStore;
          if (tableStore) selectOnRowClick(row, tableStore);
        },
      });
    } catch (error) {
      onError(error);
    }
  }

  async save() {
    try {
      const parentEntityIds = this.pageStore?.tableStore?.selected.map(
        ({ id }) => id,
      );
      if (!parentEntityIds?.length) return;
      const parenEntities = new Set<ZUid>(parentEntityIds);

      this.setResult({ status: "wait" });
      await copyEntitiesWithParents({
        entities: this.chilEntTableStore.selected.map(({ id }) => id),
        attributes: this.childAttributes,
        parentEntities: parentEntityIds,
      });
      this.pageStore?.tableStore?.setSelected([]);
      this.setResult({ status: "ready", result: true });
      if (parenEntities.has(this.entityId)) {
        await this.chilEntTableStore.reload();
      }
      await delay(600);
      this.taskStore.close();
    } catch (error) {
      this.setResult({ status: "error", error });
    }
  }

  result: RemoteData<boolean> = { status: "none" };

  setResult(data: RemoteData<boolean>) {
    this.result = data;
  }

  get isFinalStep(): boolean {
    return this.taskStore.current === CopyStep.copy;
  }
}
